import { Component, ChangeDetectionStrategy } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';

import { ZoneDeviationBannerComponent } from './shared/zone-deviation-banner/zone-deviation-banner.component';
import { ZonePlausibilityBannerComponent } from './shared/zone-plausibility-banner/zone-plausibility-banner.component';
import { DmmViolationBannerComponent } from './shared/dmm-violation-banner/dmm-violation-banner.component';
import { FtpPendingBannerComponent } from './shared/ftp-pending-banner/ftp-pending-banner.component';
import { OutlierBannerComponent } from './shared/power-outlier/outlier-banner.component';
import { FitDuplicateBannerComponent } from './shared/fit-duplicate-review/fit-duplicate-banner.component';
import { FutureSessionBannerComponent } from './shared/future-sessions/future-session-banner.component';
import { PmNoticeBannerComponent } from './tools/pm-validation/pm-notice-banner/pm-notice-banner.component';

@Component({
  standalone: true,
  selector: 'app-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    TranslateModule,
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    ZoneDeviationBannerComponent,
    ZonePlausibilityBannerComponent,
    DmmViolationBannerComponent,
    FtpPendingBannerComponent,
    OutlierBannerComponent,
    FitDuplicateBannerComponent,
    FutureSessionBannerComponent,
    PmNoticeBannerComponent,
  ],
  template: `
    <mat-toolbar color="primary" class="app-toolbar-public">
      <img
        class="app-logo"
        src="assets/logos/TRAINALYZED_horizontal_all_white.svg"
        alt="Trainalyzed"
        decoding="async" />
      <span class="spacer"></span>

      <button mat-icon-button [matMenuTriggerFor]="langMenu" aria-label="Change language">
        <mat-icon>language</mat-icon>
      </button>

      <mat-menu #langMenu="matMenu">
        <button mat-menu-item (click)="switchLang('de')">Deutsch</button>
        <button mat-menu-item (click)="switchLang('en')">English</button>
        <button mat-menu-item (click)="switchLang('es')">Español</button>
        <button mat-menu-item (click)="switchLang('fr')">Français</button>
        <button mat-menu-item (click)="switchLang('pl')">Polski</button>
        <button mat-menu-item (click)="switchLang('pt')">Português</button>
      </mat-menu>
    </mat-toolbar>
    <app-zone-deviation-banner mode="global" />
    <app-zone-plausibility-banner mode="global" />
    <app-dmm-violation-banner mode="global" />
    <app-ftp-pending-banner />
    <app-outlier-banner />
    <app-fit-duplicate-banner />
    <app-future-session-banner />
    <app-pm-notice-banner />
    <router-outlet></router-outlet>
  `,
  styles: [`
    .app-toolbar-public {
      position: sticky;
      top: 0;
      z-index: 1000;
    }
    .app-logo {
      height: 24px;
      max-width: min(220px, 52vw);
    }
    .spacer { flex: 1 1 auto; }
  `]
})
export class AppComponent {
  currentLang: string;

  constructor(private translate: TranslateService) {
    // BrandingService wird lazy von den Layout-Components instanziiert
    // (athlete-layout, main-layout). Auf AppComponent-Ebene hier NICHT
    // injecten — würde die AuthService-Construction in die Bootstrap-Phase
    // ziehen und einen Circular-DI-Zyklus mit dem jwt-Interceptor auslösen.

    // Verfügbare Sprachen registrieren (inkl. Polnisch)
    this.translate.addLangs(['de', 'en', 'es', 'fr', 'pl', 'pt']);
    this.translate.setDefaultLang('de');

    // 1) Prüfen, ob der Nutzer schon mal gewählt hat
    const saved = localStorage.getItem('lang');
    if (saved && ['de', 'en', 'es', 'fr', 'pl', 'pt'].includes(saved)) {
      this.currentLang = saved;
    } else {
      // 2) Sonst Browser-Sprache (nur der Teil vor dem '-'), Fallback auf 'de'
      const browser = this.translate.getBrowserLang()?.split('-')[0] ?? 'de';
      this.currentLang = /^(de|en|es|fr|pl|pt)$/.test(browser) ? browser : 'de';
    }

    // Sprache aktiv setzen
    this.translate.use(this.currentLang);
  }

  switchLang(lang: string) {
    this.translate.use(lang);
    this.currentLang = lang;
    localStorage.setItem('lang', lang);
  }
}
